import { create } from "zustand";

type RowMap = Map<string, Record<string, unknown>>;

function pruneAndUpsert(
  map: RowMap,
  rows: readonly { id: string }[],
  selectedIds: ReadonlySet<string>,
  getRowId?: (row: { id: string }) => string,
) {
  const rowIdFrom = getRowId ?? ((row: { id: string }) => String(row.id));

  for (const id of [...map.keys()]) {
    if (!selectedIds.has(id)) map.delete(id);
  }
  for (const row of rows) {
    const id = rowIdFrom(row);
    if (selectedIds.has(id)) {
      map.set(id, { ...(row as Record<string, unknown>) });
    }
  }
}

interface SelectedRowSnapshotsState {
  maps: Record<string, RowMap>;
  syncFromVisibleRows: (
    tableName: string,
    rows: readonly { id: string }[],
    selectedIds: ReadonlySet<string>,
    getRowId?: (row: { id: string }) => string,
  ) => void;
  getSnapshotRowsForIds: (
    tableName: string,
    ids: readonly string[],
  ) => Record<string, unknown>[];
}

export const useSelectedRowSnapshotsStore = create<SelectedRowSnapshotsState>(
  (set, get) => ({
    maps: {},
    syncFromVisibleRows(tableName, rows, selectedIds, getRowId) {
      set((state) => {
        const prev = state.maps[tableName];
        const map = new Map(prev ?? []);
        pruneAndUpsert(map, rows, selectedIds, getRowId);
        return { maps: { ...state.maps, [tableName]: map } };
      });
    },
    getSnapshotRowsForIds(tableName, ids) {
      const map = get().maps[tableName];
      if (!map) return [];
      return ids
        .map((id) => map.get(String(id)))
        .filter((r): r is Record<string, unknown> => r != null)
        .map((r) => ({ ...r }));
    },
  }),
);
